import { useEffect, useState } from 'react';
import { sources } from '../api/sources';
import { useNavigate } from 'react-router-dom';
import { Pages } from '../shared/pages';
import { PaymentType, ProductType, ProductSubType, TransactionStatus } from '@ace-clublabs/payment-types';
import Spinner from './Spinner';
import { Link, Typography } from '@ace-clublabs/shared-component-library';
import { Confirmation } from '@ace-clublabs/payments-component-library';
import { PolicyHelper } from '@ace-clublabs/payments-helper';
import {
  ErrorType,
  ICardBrandMap,
  IConfirmationApiRequest,
  IConfirmationApiResponse,
  PolicyType,
} from '../shared/types';
import { getClubPhoneNumber } from '../utils/methods';
import { useGlobalState } from '../shared/state';
import { Header } from './Header';
import { UtagHelper } from '../utils/utagHelper';
import { PaymentTypeTagging, ProductTypeTagging } from '../shared/enums';

export const ConfirmationPage: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setLoading] = useState(true);
  const [transactionCheckMade, setTransactionCheckMade] = useGlobalState('transactionCheckMade');
  const [emailFromWeb] = useGlobalState('emailFromWeb');
  const [emailOnFile] = useGlobalState('emailOnFile');
  const [billPlan] = useGlobalState('billPlan');
  const [confirmationDetails, setConfirmationDetails] = useGlobalState('confirmationDetails');
  const [selectedStoredMethod] = useGlobalState('selectedStoredMethod');
  const [selectedProduct] = useGlobalState('selectedProduct');
  const [sessionId] = useGlobalState('sessionId');
  const [confirmationVisited, setConfirmationVisited] = useGlobalState('confirmationVisited');
  const [memcid, setMemCid] = useGlobalState('memcid');

  const productType =
    selectedProduct?.policyType === PolicyType.Membership ? ProductType.Membership : ProductType.Insurance;

  let request: IConfirmationApiRequest = {
    productId: selectedProduct.productId,
    isTransactionCheck: true,
    productType,
    transactionId: '',
    memcid: memcid ? memcid : undefined,
  };

  const updateConfirmationResponse = (confirmation: IConfirmationApiResponse) => {
    if (
      (confirmation.paymentDetails?.paymentType === PaymentType.CreditOrDebit ||
        confirmation.paymentDetails?.paymentType === PaymentType.DebitOnly) &&
      !confirmation.paymentDetails?.card
    ) {
      confirmation.paymentDetails.card = {
        last4: selectedStoredMethod?.last4!,
        brand: selectedStoredMethod?.brand!,
      };
    } else if (
      confirmation.paymentDetails?.paymentType === PaymentType.Echeck &&
      !confirmation.paymentDetails?.echeck
    ) {
      confirmation.paymentDetails.echeck = {
        accountLast4: selectedStoredMethod?.accountLast4!,
        routingNumber: selectedStoredMethod?.routingNumber!,
      };
    }
  };

  const tagConfirmation = (confirmation: IConfirmationApiResponse, confirmationVisited: boolean) => {
    const { paymentDetails, confirmationNumber, autoPayConfirmationNumber, clubCode } = confirmation;
    const { amountPaid, paymentType, paymentStatus, storePaymentStatus, autoPayStatus } = paymentDetails || {};
    const amount = amountPaid ? amountPaid.toFixed(2) : '0.00';

    const autoPaySuccessWithAmountPaid = autoPayStatus === TransactionStatus.Succeeded && amountPaid! > 0;
    const storePaymentSucceeded = storePaymentStatus && storePaymentStatus === TransactionStatus.Succeeded;
    const paymentOkAutoPayFail: boolean =
      paymentStatus === TransactionStatus.Succeeded && autoPayStatus !== TransactionStatus.Succeeded;

    if (confirmationVisited) {
      let visitedTags: any = {
        ...UtagHelper.CommonTaggingValues,
        clubcode: clubCode,
        page: UtagHelper.Pages.confirmationReload,
        sessionid: sessionId,
        mem_billplan: billPlan,
        ...(selectedProduct && { flow: productType === ProductType.Membership ? 'membership' : 'insurance' }),
      };
      UtagHelper.tag(visitedTags);
    } else {
      let defaultTags: any = {
        ...UtagHelper.CommonTaggingValues,
        autopayconfirmation: autoPayConfirmationNumber ? autoPayConfirmationNumber : '',
        clubcode: clubCode,
        confirmation_id: '',
        transaction_type: getPaymentTypeTagging(paymentType!),
        sessionid: sessionId,
        page: UtagHelper.Pages.confirmation,
        category:
          productType === ProductType.Membership ? ['membership:manage auto pay'] : ['insurance:manage auto pay'],
        product: [ProductTypeTagging.AutoPay],
        quantity: ['1'],
        transaction_amount: [amount],
        autopay: 'true',
        payment_method_saved: storePaymentSucceeded ? 'true' : 'false',
        mem_billplan: billPlan,
        ...(selectedProduct && { flow: productType === ProductType.Membership ? 'membership' : 'insurance' }),
      };

      if (paymentOkAutoPayFail) {
        defaultTags = {
          ...defaultTags,
          confirmation_id: confirmationNumber,
          page: UtagHelper.Pages.confirmationError,
          product: [ProductTypeTagging.Payment],
          autopay: 'false',
          error: 'auto pay failed',
        };
      }

      if (autoPaySuccessWithAmountPaid) {
        defaultTags = {
          ...defaultTags,
          confirmation_id: confirmationNumber,
          category:
            productType === ProductType.Membership
              ? ['membership:manage auto pay', 'membership:manage auto pay']
              : ['insurance:manage auto pay', 'insurance:manage auto pay'],
          product: [ProductTypeTagging.Payment, ProductTypeTagging.AutoPay],
          quantity: ['1', '1'],
          transaction_amount: [amount, '0.00'],
        };
      }

      UtagHelper.tag(defaultTags);
    }
  };

  const checkConfirmationStatus = async (transactionId: string) => {
    try {
      setLoading(true);
      request = { ...request, transactionId };
      const confirmation = await sources.confirmation(request);
      updateConfirmationResponse(confirmation);

      setTransactionCheckMade(true);
      setConfirmationDetails(confirmation);
      setLoading(false);
    } catch (e: any) {
      navigate(Pages.Error, { state: { type: ErrorType.SYSTEM_ERROR } });
    }
  };

  const onLoad = async () => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const transactionId = urlParams.get('transactionId')!;
      const memcidInUrl = urlParams.get('memcid') || '';
      setMemCid(memcidInUrl);

      if (!transactionCheckMade) {
        await checkConfirmationStatus(transactionId);
      }

      if (!emailOnFile && !emailFromWeb) {
        return navigate(`${Pages.UpdateEmail}?transactionId=${transactionId}`);
      }

      setLoading(true);
      request = {
        ...request,
        isTransactionCheck: false,
        transactionId,
        email: emailFromWeb,
      };

      const result = await sources.confirmation(request);
      updateConfirmationResponse(result);

      tagConfirmation(result, confirmationVisited);
      setConfirmationVisited(true);
      setLoading(false);
    } catch (e: any) {
      navigate(Pages.Error, { state: { type: ErrorType.SYSTEM_ERROR } });
    }
  };

  useEffect(() => {
    onLoad();
  }, []);

  return (
    <>
      <Header />
      {isLoading && <Spinner />}
      {!isLoading && <ConfirmationView confirmationDetails={confirmationDetails!} productType={productType} />}
    </>
  );
};

interface ConfirmationViewProps {
  confirmationDetails: IConfirmationApiResponse;
  productType: ProductType;
}

export const ConfirmationView: React.FC<ConfirmationViewProps> = (props) => {
  const navigate = useNavigate();
  const [emailFromWeb] = useGlobalState('emailFromWeb');

  const {
    email,
    firstName,
    clubCode,
    isMembershipInBlackoutPeriod,
    productId,
    paymentDetails: { amountPaid, paymentStatus, autoPayStatus, storePaymentStatus } = {},
  } = props.confirmationDetails;

  const clubPhoneNumber = getClubPhoneNumber(clubCode);

  const paymentOkAutoPayFail: boolean =
    paymentStatus === TransactionStatus.Succeeded && autoPayStatus !== TransactionStatus.Succeeded;

  const standAloneAutoPay: boolean = !(amountPaid && amountPaid > 0);

  const paymentOkAutoPayFailStorePaymentFail =
    paymentOkAutoPayFail && storePaymentStatus && storePaymentStatus !== TransactionStatus.Succeeded;

  const headerText = paymentOkAutoPayFail
    ? 'Thank you for your payment'
    : standAloneAutoPay
    ? 'Thank you for enrolling in Auto Pay'
    : 'Thank you for your payment and Auto Pay enrollment';

  const autoPayText = isMembershipInBlackoutPeriod
    ? `Starting with your next billing cycle, we'll begin automatically charging or debiting your selected payment method.`
    : `We'll begin automatically charging or debiting your selected payment method.`;

  let confirmationProps = {
    title: headerText,
    confirmationDetails: {
      ...props.confirmationDetails,
      emails: { primaryEmail: email || emailFromWeb },
      confirmationNumber:
        props.confirmationDetails.confirmationNumber || props.confirmationDetails.autoPayConfirmationNumber,
    },
    displayScheduledPayment: false,
    errorMessage: paymentOkAutoPayFailStorePaymentFail ? (
      <>
        Sorry, we could not complete your Auto Pay enrollment or save your selected payment method.{' '}
        <Link variant="b1Regular" target="_self" href={`${Pages.ManageAutoPay}`}>
          Manage Auto Pay
        </Link>
        .
      </>
    ) : paymentOkAutoPayFail ? (
      <Typography variant="b2Regular">
        Please try again later, or call us at{' '}
        <Link variant="b2Regular" target="_self" href={`tel:${clubPhoneNumber}`}>
          {clubPhoneNumber}
        </Link>{' '}
        to enroll in Auto Pay.
      </Typography>
    ) : undefined,
    discontinueAutoPayMessage: !paymentOkAutoPayFail ? true : false,
    autoPayMessage: !paymentOkAutoPayFail ? (
      <>
        <strong>Auto Pay: Enrolled</strong> — {autoPayText}
      </>
    ) : undefined,
    phoneNumber: clubPhoneNumber,
    productType: props.productType,
    button: { primary: { buttonText: `Go to manage Auto Pay`, onClick: () => navigate(Pages.ManageAutoPay) } },
    showAcquisitionBanner: {
      enabledBanners:
        PolicyHelper.getProductInfoFromPolicyNumber(productId)?.productSubType === ProductSubType.Auto
          ? [ProductSubType.Home]
          : [ProductSubType.Auto],
    },
  };

  const getAAAVisaOfferText = () => {
    if (clubCode === '004' && props.productType === ProductType.Insurance) {
      return undefined;
    }
    const title = `Hey ${firstName ? firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase() : 'member'},`;
    const description =
      amountPaid && amountPaid > 0
        ? `You could be earning up to <savingsAmount> cash back using your AAA Visa Signature® credit card.`
        : 'Earn cash back on everyday purchases everywhere using your AAA Visa Signature® credit card.';
    const savingsAmount = amountPaid && amountPaid > 0 ? `${(amountPaid * 0.03).toFixed(2)}` : '';
    return { title, description, linkText: 'Learn more', savingsAmount };
  };

  return <Confirmation {...confirmationProps} aaaVisaOffer={getAAAVisaOfferText()} />;
};

export const CARD_BRAND_MAP: ICardBrandMap = {
  visa: 'Visa',
  mastercard: 'MasterCard',
  discover: 'Discover',
  'american-express': 'American Express',
};

export const getPaymentTypeTagging = (paymentType: PaymentType): PaymentTypeTagging | undefined => {
  if (paymentType === PaymentType.CreditOrDebit) {
    return PaymentTypeTagging.CreditCard;
  } else if (paymentType === PaymentType.DebitOnly) {
    return PaymentTypeTagging.DebitCard;
  } else if (paymentType === PaymentType.Echeck) {
    return PaymentTypeTagging.Echeck;
  } else {
    return undefined;
  }
};
